<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Navbar -->
    <div class="bg-white shadow-sm sticky top-0 z-40">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Mobile Layout -->
        <div class="lg:hidden">
          <div class="flex flex-col py-3">
            <div class="flex justify-between items-center">
              <div class="flex-shrink-0">
                <img class="h-6 w-auto" src="/assets/images/CHECK-APP.png" alt="Logo">
              </div>
              <div class="flex items-center">
                <button @click="$router.push('/')" class="text-blue-600 hover:text-blue-800 text-sm">
                  <i class="fas fa-home mr-1"></i> {{ $t('searchResults.home') }}
                </button>
              </div>
            </div>
            <div class="mt-3">
              <div class="relative text-gray-600 focus-within:text-gray-900">
                <div class="flex bg-white rounded-full shadow-sm border border-gray-300 hover:border-blue-500 transition-all duration-300">
                  <span class="flex items-center pl-3">
                    <button @click="performSearch" type="submit" class="p-1 focus:outline-none focus:shadow-outline">
                      <i class="fas fa-search text-gray-500 text-sm"></i>
                    </button>
                  </span>
                  <input
                    v-model="searchTerm"
                    @keyup.enter="performSearch"
                    type="search"
                    class="w-full py-2 text-sm text-black focus:outline-none rounded-full pl-2 pr-6"
                    :placeholder="$t('searchResults.placeholder')"
                    autocomplete="off"
                  />
                  <button v-if="searchTerm" @click="clearSearch" class="flex items-center pr-3 text-gray-500 hover:text-gray-700">
                    <i class="fas fa-times text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Layout -->
        <div class="hidden lg:flex justify-between h-16 items-center">
          <div class="flex-shrink-0 flex items-center">
            <img class="h-8 w-auto" src="/assets/images/CHECK-APP.png" alt="Logo">
          </div>
          <div class="flex-1 max-w-2xl mx-4">
            <div class="relative text-gray-600 focus-within:text-gray-900">
              <div class="flex bg-white rounded-full shadow-sm border border-gray-300 hover:border-blue-500 transition-all duration-300">
                <span class="flex items-center pl-4">
                  <button @click="performSearch" type="submit" class="p-1 focus:outline-none focus:shadow-outline">
                    <i class="fas fa-search text-gray-500"></i>
                  </button>
                </span>
                <input
                  v-model="searchTerm"
                  @keyup.enter="performSearch"
                  type="search"
                  class="w-full py-2 text-sm text-black focus:outline-none rounded-full pl-3 pr-8"
                  :placeholder="$t('searchResults.placeholder')"
                  autocomplete="off"
                />
                <button v-if="searchTerm" @click="clearSearch" class="flex items-center pr-4 text-gray-500 hover:text-gray-700">
                  <i class="fas fa-times text-sm"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="flex items-center">
            <button @click="$router.push('/')" class="text-blue-600 hover:text-blue-800">
              <i class="fas fa-home mr-1"></i> {{ $t('searchResults.home') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div v-if="isNavigating" class="fixed inset-0 bg-white bg-opacity-75 flex flex-col items-center justify-center z-50">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-800"></div>
        <p class="mt-4 text-gray-600 font-medium">{{ $t('loading') }}</p>
      </div>

      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-800"></div>
      </div>

      <div v-else-if="error" class="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
        <div class="flex">
          <div class="flex-shrink-0"><svg class="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" /></svg></div>
          <div class="ml-3"><p class="text-sm text-red-700">{{ error }}</p></div>
        </div>
      </div>

      <div v-else-if="searchTerm && !isLoading" class="flex flex-col lg:flex-row gap-6">
        <!-- Left Column - Main Results -->
        <div class="flex-1">
           <div class="mb-6">
              <p class="text-sm text-gray-600">{{ searchResultHeaderText }}</p>
              <div v-if="didYouMean && didYouMean.suggestions && didYouMean.suggestions.length > 0" class="mt-2 text-sm">
                <span class="text-gray-700">{{ $t('searchResults.didYouMean') }}</span>
                <button v-for="suggestion in didYouMean.suggestions" :key="suggestion" @click="searchSuggestion(suggestion)" class="ml-2 text-blue-600 hover:text-blue-800 font-semibold hover:underline">{{ suggestion }}</button>
              </div>
            </div>

          <div v-if="paginatedResults.length > 0" class="space-y-8">
            <div v-for="result in paginatedResults" :key="result.uniqueId" class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <!-- Main Card Header -->
              <div class="p-4 cursor-pointer hover:bg-gray-50" @click="navigateToResult(result)">
                <div class="flex items-start">
                  <div class="flex-shrink-0 h-12 w-12 rounded-full bg-gray-100 overflow-hidden mr-4 border border-gray-200">
                    <img :src="result.imageUrl" :alt="result.name" class="h-full w-full object-cover" @error="handleImageError"/>
                  </div>
                  <div class="flex-1 min-w-0 text-left">
                    <h2 class="text-xl font-bold text-blue-800 mb-1 hover:underline">
                      {{ result.name }}
                      <span v-if="result.type === 'Organization' && isValidDescription(result.abbreviation)" class="text-gray-500 text-sm ml-2">({{ result.abbreviation }})</span>
                    </h2>
                    <!-- Conformity star rating for private structures -->
                    <div
                      v-if="result.type === 'Organization' && result.originalData?.related_data?.structure?.type === 'private' && result.conformitySummary"
                      class="conformity-stars-inline"
                    >
                      <template v-for="n in 5" :key="n">
                        <i
                          class="fas"
                          :class="{
                            'fa-star conformity-star-filled': n <= Math.floor(result.conformitySummary.star_rating || 0),
                            'fa-star-half-alt conformity-star-filled': n === Math.ceil(result.conformitySummary.star_rating) && (result.conformitySummary.star_rating % 1) >= 0.5,
                            'fa-star conformity-star-empty': n > Math.ceil(result.conformitySummary.star_rating || 0)
                          }"
                        ></i>
                      </template>
                      <i v-if="result.conformitySummary.is_physically_compliant" class="fas fa-check-circle conformity-verified-icon" title="Physically Verified"></i>
                    </div>
                    <p v-if="result.type === 'Public Personality' && isValidDescription(result.title)" class="text-sm text-gray-600 font-medium -mt-1 mb-1">{{ result.title }}</p>
                    <p v-if="isValidDescription(result.description)" class="mt-1 text-sm text-gray-600 line-clamp-2">{{ result.description }}</p>
                  </div>
                </div>
              </div>

              <!-- Section Previews -->
              <div class="space-y-1">
                <!-- Head of Organization / Ambassador -->
                <div v-if="result.headPerson && result.type !== 'Public Personality'" class="p-4 hover:bg-gray-50 border-t border-gray-200 cursor-pointer" @click="navigateToResult(result, 'boss')">
                  <div class="flex items-start ml-2">
                    <div class="flex-1 min-w-0 text-left">
                      <div class="flex items-center">
                        <div class="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 overflow-hidden mr-3 border"><img :src="getImageUrl(result.headPerson.photo)" :alt="result.headPerson.name" class="h-full w-full object-cover" @error="handleImageError"/></div>
                        <div class="flex-1 min-w-0"><p v-if="isValidDescription(result.headPerson.name)" class="font-semibold text-gray-900 truncate">{{ result.headPerson.name }}</p><p v-if="isValidDescription(result.headPerson.title)" class="text-sm text-gray-500 truncate">{{ result.headPerson.title }}</p></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Missions / Services Section -->
                <div v-if="result.missions && result.missions.length > 0" class="p-4 hover:bg-gray-50 border-t border-gray-200 cursor-pointer" @click="navigateToResult(result, 'missions')">
                  <div class="flex items-start ml-2">
                    <div class="flex-shrink-0 w-8 text-center"><i class="fas fa-bullseye-pointer text-gray-500 text-lg"></i></div>
                    <div class="flex-1 min-w-0 text-left">
                      <h3 class="text-base font-semibold text-gray-800 mb-2">{{ result.type === 'Embassy' ? $t('searchResults.servicesOf') : $t('searchResults.missionsOf') }}</h3>
                      <ul class="space-y-2 text-sm text-gray-700">
                        <li v-for="mission in result.missions.slice(0, 2)" :key="mission.id">
                          <p v-if="mission.title" class="font-semibold text-gray-800 truncate">{{ mission.title }}</p>
                          <p v-if="mission.description" class="line-clamp-2">{{ mission.description }}</p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <!-- State Personnels -->
                <div v-if="result.personnels && result.personnels.length > 0" class="p-4 hover:bg-gray-50 border-t border-gray-200 cursor-pointer" @click="navigateToResult(result, 'key-personnels')">
                  <div class="flex items-start ml-2">
                    <div class="flex-shrink-0 w-8 text-center"><i class="fas fa-users text-gray-500 text-lg"></i></div>
                    <div class="flex-1 min-w-0 text-left">
                      <h3 class="text-base font-semibold text-gray-800 mb-2">{{ $t('searchResults.personnelOf') }}</h3>
                      <ul class="space-y-1 text-sm text-gray-600">
                        <li v-for="person in result.personnels.slice(0, 2)" :key="person.id" class="truncate">{{ person.name }} - <span class="text-gray-500">{{ person.title }}</span></li>
                         <li v-if="result.personnels.length > 2" class="font-semibold text-blue-700">... {{ $t('searchResults.andMore', { count: result.personnels.length - 2 }) }}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Regional Delegations -->
                <div v-if="result.regionals && result.regionals.length > 0" class="p-4 hover:bg-gray-50 border-t border-gray-200 cursor-pointer" @click="navigateToResult(result, 'regional')">
                   <div class="flex items-start ml-2">
                    <div class="flex-shrink-0 w-8 text-center"><i class="fas fa-map-signs text-gray-500 text-lg"></i></div>
                    <div class="flex-1 min-w-0 text-left">
                       <h3 class="text-base font-semibold text-gray-800 mb-2">{{ $t('searchResults.regionalsOf') }}</h3>
                       <p class="text-sm text-gray-600">{{ result.regionals.slice(0, 3).map(r => r.name).join(', ') }}<span v-if="result.regionals.length > 3">...</span></p>
                    </div>
                   </div>
                </div>

                <!-- Sub-Structures -->
                <div v-if="result.subStructures && result.subStructures.length > 0" class="p-4 hover:bg-gray-50 border-t border-gray-200 cursor-pointer" @click="navigateToResult(result, 'sub-structures')">
                  <div class="flex items-start ml-2">
                    <div class="flex-shrink-0 w-8 text-center"><i class="fas fa-sitemap text-gray-500 text-lg"></i></div>
                    <div class="flex-1 min-w-0 text-left">
                      <h3 class="text-base font-semibold text-gray-800 mb-2">{{ $t('subStructures') }} ({{ result.subStructures.length }})</h3>
                      <div class="flex flex-wrap gap-2">
                        <span
                          v-for="child in result.subStructures.slice(0, 4)"
                          :key="child.id"
                          class="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full truncate max-w-[140px]"
                        >{{ child.abbreviation || child.name }}</span>
                        <span v-if="result.subStructures.length > 4" class="text-xs text-gray-500 font-medium self-center">+{{ result.subStructures.length - 4 }} more</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Contacts -->
                <div v-if="result.contacts && (isValidDescription(result.contacts.website) || isValidDescription(result.contacts.email) || isValidDescription(result.contacts.phone))" class="p-4 hover:bg-gray-50 border-t border-gray-200 cursor-pointer" @click="navigateToResult(result, 'contact')">
                    <div class="flex items-start ml-2">
                        <div class="flex-shrink-0 w-8 text-center"><i class="fas fa-address-book text-gray-500 text-lg"></i></div>
                        <div class="flex-1 min-w-0 text-left">
                          <h3 class="text-base font-semibold text-gray-800 mb-2">{{ $t('searchResults.contactsAndSocial') }}</h3>
                          <div class="space-y-2 text-sm">
                              <div v-if="isValidDescription(result.contacts.website)" class="flex items-center"><i class="fas fa-globe w-4 mr-2 text-gray-500"></i><a :href="formatWebsiteUrl(result.contacts.website)" @click.stop target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline truncate">{{ result.contacts.website }}</a></div>
                              <div v-if="isValidDescription(result.contacts.email)" class="flex items-center"><i class="fas fa-envelope w-4 mr-2 text-gray-500"></i><a :href="`mailto:${result.contacts.email}`" @click.stop class="text-blue-600 hover:underline">{{ result.contacts.email }}</a></div>
                              <div v-if="isValidDescription(result.contacts.phone)" class="flex items-center"><i class="fas fa-phone-alt w-4 mr-2 text-gray-500"></i><a :href="`tel:${result.contacts.phone}`" @click.stop class="text-blue-600 hover:underline">{{ result.contacts.phone }}</a></div>
                          </div>
                        </div>
                    </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-if="!isLoading && allResults.length === 0" class="text-center py-12">
            <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            <h3 class="mt-4 text-xl font-medium text-gray-800">{{ $t('searchResults.noResults.title') }}</h3>
            <p class="mt-2 text-gray-600">{{ $t('searchResults.noResults.subtitle', { searchTerm: searchTerm }) }}</p>
          </div>

          <div v-if="totalPages > 1" class="mt-8 flex justify-center items-center space-x-2">
            <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" class="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{{ $t('searchResults.pagination.previous') }}</button>
            <button v-for="page in paginationRange" :key="page" @click="goToPage(page)" :class="['px-3 py-1 rounded-md text-sm font-medium', page === currentPage ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50']">{{ page }}</button>
            <button @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages" class="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{{ $t('searchResults.pagination.next') }}</button>
          </div>
        </div>

        <!-- Right Column - Sidebar -->
        <div v-if="paginatedResults.length > 0" class="lg:w-80 xl:w-96 flex-shrink-0 hidden lg:block">
          <div class="sticky top-24 space-y-4">
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div class="h-48 bg-gray-100 flex items-center justify-center relative">
                <div v-if="sidebarMapUrl" class="w-full h-full">
                  <a 
                    :href="paginatedResults[0].contacts?.google_map_directions || getGoogleMapsLink(paginatedResults[0].location)" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <img 
                      :src="sidebarMapUrl" 
                      :alt="`Map of ${paginatedResults[0].name}`" 
                      class="w-full h-full object-cover"
                    />
                  </a>
                </div>
                <img 
                  v-else-if="paginatedResults[0].imageUrl" 
                  :src="paginatedResults[0].imageUrl" 
                  :alt="paginatedResults[0].name" 
                  class="h-full w-full object-cover" 
                  @error="handleImageError"
                />
                <div v-else class="text-gray-400 text-center">
                  <i class="fas fa-map-marked-alt text-4xl"></i>
                  <p class="mt-2 text-sm">{{ $t('searchResults.sidebar.noMap') }}</p>
                </div>
              </div>
              <div class="p-4">
                <h3 class="text-lg font-medium text-gray-900 mb-2">{{ paginatedResults[0].name }}</h3>
                <div v-if="paginatedResults[0].contacts || paginatedResults[0].location" class="space-y-3 text-sm">
                  <div v-if="isValidDescription(paginatedResults[0].location)" class="flex items-start">
                    <i class="fas fa-map-marker-alt text-gray-500 mt-1 mr-2 w-4"></i>
                    <span>{{ paginatedResults[0].location }}</span>
                  </div>
                  <div v-if="isValidDescription(paginatedResults[0].contacts?.website)" class="flex items-center">
                    <i class="fas fa-globe text-gray-500 mr-2 w-4"></i>
                    <a :href="formatWebsiteUrl(paginatedResults[0].contacts.website)" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline truncate">{{ paginatedResults[0].contacts.website }}</a>
                  </div>
                  <div v-if="isValidDescription(paginatedResults[0].contacts?.phone)" class="flex items-center">
                    <i class="fas fa-phone-alt text-gray-500 mr-2 w-4"></i>
                    <a :href="`tel:${paginatedResults[0].contacts.phone}`" class="text-blue-600 hover:underline">{{ paginatedResults[0].contacts.phone }}</a>
                  </div>
                  <div v-if="paginatedResults[0].location" class="pt-2">
                    <button @click="openNavigation(paginatedResults[0].location)" class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"><i class="fas fa-route mr-2"></i> {{ $t('searchResults.sidebar.getDirections') }}</button>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div class="p-4">
                <h3 class="text-lg font-medium text-gray-900 mb-3">{{ $t('searchResults.sidebar.quickLinks.title') }}</h3>
                <ul class="space-y-2">
                  <li v-for="link in computedQuickLinks" :key="link.text"><a :href="link.url" target="_blank" rel="noopener noreferrer" class="flex items-center text-sm text-blue-600 hover:underline"><i :class="`${link.icon} mr-2 text-gray-500`"></i>{{ link.text }}</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!searchTerm" class="text-center py-12">
        <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
        <h3 class="mt-4 text-xl font-medium text-gray-800">{{ $t('searchResults.initial.title') }}</h3>
        <p class="mt-2 text-gray-600">{{ $t('searchResults.initial.subtitle') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useSearchStore } from '@/stores/search';
import { useStructureStore } from '@/stores/structure';
import { usePublicPersonalityStore } from '@/stores/personalityStore';
import { useEmbassyStore } from '@/stores/embassy'; 

const BaseURL = process.env.VUE_APP_API_BASE_URL;
const RESULTS_PER_PAGE = 5;

export default {
  name: "SearchResults",
  data() {
    return {
      searchTerm: '',
      allResults: [],
      isLoading: false,
      isNavigating: false,
      error: null,
      currentPage: 1,
      didYouMean: null,
      searchIntent: null,
      searchedInAlternateLang: false,
      resultsLanguage: null,
    };
  },
  computed: {
    currentLanguage() { return this.$i18n.locale; },
    resultsLanguageName() {
      return this.resultsLanguage === 'fr' ? this.$t('langNameFr') : this.$t('langNameEn');
    },
    currentLanguageName() {
      return this.currentLanguage === 'fr' ? this.$t('langNameFr') : this.$t('langNameEn');
    },
    totalResults() { return this.allResults.length; },
    totalPages() { return Math.ceil(this.totalResults / RESULTS_PER_PAGE); },
    paginatedResults() {
      const start = (this.currentPage - 1) * RESULTS_PER_PAGE;
      const end = start + RESULTS_PER_PAGE;
      return this.allResults.slice(start, end);
    },
    sidebarMapUrl() {
      if (!this.paginatedResults.length) return null;
      const location = this.paginatedResults[0].location;
      if (!this.isValidDescription(location)) return null;
      
      const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        return null;
      }
      
      return `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(location)}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${encodeURIComponent(location)}&key=${apiKey}`;
    },
    computedQuickLinks() {
        let links = [
            { text: this.$t('searchResults.sidebar.quickLinks.govPortal'), url: 'https://www.spm.gov.cm/', icon: 'fas fa-landmark' },
            { text: this.$t('searchResults.sidebar.quickLinks.gazette'), url: 'https://www.prc.cm/en/official-journal', icon: 'fas fa-newspaper' },
            { text: this.$t('searchResults.sidebar.quickLinks.assembly'), url: 'https://www.assnat.cm', icon: 'fas fa-university' },
        ];
        if (this.paginatedResults.length > 0) {
            const firstResult = this.paginatedResults[0];
            if (firstResult.name && firstResult.contacts && this.isValidDescription(firstResult.contacts.website)) {
                links.splice(1, 0, { text: firstResult.name, url: this.formatWebsiteUrl(firstResult.contacts.website), icon: 'fas fa-globe' });
            }
        }
        return links;
    },
    searchResultHeaderText() {
        if (this.totalResults === 0) return this.$t('searchResults.header.noResults', { searchTerm: this.searchTerm });
        let intentKey;
        switch (this.searchIntent) {
            case 'who': intentKey = 'searchResults.header.peopleMatching'; break;
            case 'where': intentKey = 'searchResults.header.locationsFor'; break;
            case 'contact': intentKey = 'searchResults.header.contactsFor'; break;
            default: intentKey = '';
        }
        const intentText = intentKey ? this.$t(intentKey) : '';
        const entity = (this.didYouMean?.suggestions?.length > 0) ? this.didYouMean.suggestions[0] : `"${this.searchTerm}"`;
        return this.$t('searchResults.header.showingResults', { total: this.totalResults, intentText: intentText, entity: entity });
    },
    paginationRange() {
        const range = [], total = this.totalPages, current = this.currentPage;
        if (total <= 7) { for (let i = 1; i <= total; i++) range.push(i); } 
        else {
            range.push(1);
            if (current > 3) range.push('...');
            const start = Math.max(2, current - 1), end = Math.min(total - 1, current + 1);
            for (let i = start; i <= end; i++) range.push(i);
            if (current < total - 2) range.push('...');
            range.push(total);
        }
        return range.filter((p, i) => p !== '...' || range[i-1] !== '...');
    }
  },
  async created() {
    const store = useSearchStore();
    this.searchTerm = this.$route.query.q || store.searchTerm || '';
    if (this.searchTerm) await this.performSearch();
  },
  methods: {
    getImageUrl(photoPath) {
      if (!photoPath || typeof photoPath !== 'string') return '/assets/images/default-avatar.jpg';
      if (photoPath.startsWith('http')) return photoPath;
      return `${BaseURL}${photoPath.startsWith('/') ? '' : '/'}${photoPath}`;
    },
    handleImageError(event) { event.target.src = '/assets/images/default-avatar.jpg'; },
    isValidDescription(desc) {
      if (desc === null || typeof desc === 'undefined') return false;
      const val = String(desc).toLowerCase().trim();
      return !['nan', 'none', 'null', ''].includes(val);
    },
    formatWebsiteUrl(url) {
      if (!url) return '#';
      if (!url.startsWith('http://') && !url.startsWith('https://')) return `https://${url}`;
      return url;
    },
    getGoogleMapsLink(location) {
      if (!location) return '#';
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
    },
    openNavigation(location) {
        if (!location) return;
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const destination = encodeURIComponent(location);
                    window.open(`https://www.google.com/maps/dir/?api=1&origin=${latitude},${longitude}&destination=${destination}`, '_blank', 'noopener,noreferrer');
                },
                () => window.open(this.getGoogleMapsLink(location), '_blank', 'noopener,noreferrer')
            );
        } else {
            window.open(this.getGoogleMapsLink(location), '_blank', 'noopener,noreferrer');
        }
    },
    _normalizeResult(item) {
        const rd = item.related_data || {};
        let resultType, structure, name;

        switch (item.model_type) {
            case 'PublicPersonality':
                resultType = 'Public Personality';
                break;
            case 'Embassy':
            case 'Ambassador':
                resultType = 'Embassy';
                break;
            default:
                resultType = 'Organization';
        }

        if (resultType === 'Organization') {
            structure = rd.structure || {};
            name = item.model_type === 'StatePersonnels' ? (structure.name || 'Related Organization') : (structure.name || item.name || 'Unnamed Entry');
        } else {
            structure = {};
            name = item.name || 'Unnamed Entry';
        }

        return {
            uniqueId: `${item.model_type}-${item.id}`,
            id: item.id,
            modelType: item.model_type,
            type: resultType,
            name: name,
            title: item.title,
            country: item.country,
            abbreviation: structure.abbreviation,
            description: structure.description || item.description,
            imageUrl: this.getImageUrl(structure.photo || item.photo),
            location: rd.contacts?.location || item.location,
            headPerson: item.model_type === 'Boss' ? item : (rd.boss || rd.ambassador),
            missions: rd.missions || rd.services || [],
            regionals: rd.regionals || [],
            personnels: rd.personnels || [],
            contacts: rd.contacts,
            subStructures: rd.structure?.sub_structures || [],
            originalData: item
        };
    },
    async performSearch() {
      if (!this.searchTerm.trim()) {
        this.allResults = []; this.didYouMean = null; this.searchIntent = null;
        this.searchedInAlternateLang = false; this.resultsLanguage = null;
        return;
      }
      this.isLoading = true; this.error = null; this.currentPage = 1;
      this.searchedInAlternateLang = false;
      this.resultsLanguage = this.currentLanguage;

      const currentLang = this.currentLanguage;
      const altLang = currentLang === 'fr' ? 'en' : 'fr';

      try {
        // Fire BOTH language requests in parallel — same mechanism as the landing page
        const [primaryRes, altRes] = await Promise.allSettled([
          axios.get(`${BaseURL}/search/`, {
            params: { search_term: this.searchTerm },
            headers: { 'Accept-Language': currentLang }
          }),
          axios.get(`${BaseURL}/search/`, {
            params: { search_term: this.searchTerm },
            headers: { 'Accept-Language': altLang }
          }),
        ]);

        const primaryData = primaryRes.status === 'fulfilled' ? primaryRes.value.data : null;
        const altData = altRes.status === 'fulfilled' ? altRes.value.data : null;

        // Process both result sets independently
        const primaryProcessed = primaryData ? this._processSearchResponse(primaryData) : [];
        const altProcessed = altData ? this._processSearchResponseClean(altData) : [];

        // --- MERGE both language results (same approach as landing page) ---
        // Deduplicate by unique key, keeping the highest-scoring version
        const mergedMap = new Map();

        // Add primary language results first
        for (const item of primaryProcessed) {
          const key = this._getResultUniqueKey(item);
          if (!key) continue;
          mergedMap.set(key, { ...item, _fromLang: currentLang });
        }

        // Add alternate language results — only if score is higher than existing
        for (const item of altProcessed) {
          const key = this._getResultUniqueKey(item);
          if (!key) continue;
          const existing = mergedMap.get(key);
          const newScore = item.originalData?.match_score || 0;
          const existingScore = existing?.originalData?.match_score || 0;
          if (!existing || newScore > existingScore) {
            mergedMap.set(key, { ...item, _fromLang: altLang });
          }
        }

        // Sort merged results by match_score descending — keep only the top match
        const merged = Array.from(mergedMap.values())
          .sort((a, b) => (b.originalData?.match_score || 0) - (a.originalData?.match_score || 0))
          .slice(0, 1);

        this.allResults = merged;

        // Determine which language produced the top result
        const topResult = merged[0];
        if (topResult && topResult._fromLang && topResult._fromLang !== currentLang) {
          this.searchedInAlternateLang = true;
          this.resultsLanguage = topResult._fromLang;
        }

        // ── Direct Navigation: only for person-type results (Boss, StatePersonnels, PublicPersonality, Ambassador) ──
        // Structures always go through the search results page first.
        const PERSON_TYPES = ['Boss', 'StatePersonnels', 'PublicPersonality', 'Ambassador'];
        const primaryTopScore = primaryProcessed.length > 0 ? (primaryProcessed[0].originalData?.match_score || 0) : 0;
        const altTopScore = altProcessed.length > 0 ? (altProcessed[0].originalData?.match_score || 0) : 0;
        const bestData = altTopScore > primaryTopScore ? altData : primaryData;
        if (bestData) {
          this.didYouMean = bestData.did_you_mean;
          this.searchIntent = bestData.intent;

          const direct = bestData.direct_navigation;
          const directModelType = direct?.related_data?.model_type || direct?.model_type;
          if (direct && direct.route_path && PERSON_TYPES.includes(directModelType)) {
            const navLang = this.currentLanguage;
            const rd = direct.related_data || {};
            const structure = rd.structure;
            if (structure) {
              const { useStructureStore } = await import('@/stores/structure');
              const store = useStructureStore();
              store.clearCurrentStructure();
              store.currentStructureId = structure.id;
              store.currentStructureName = structure.name;
            }
            const section = direct.route_params?.section;
            const path = `/${navLang}${direct.route_path}`;
            const query = section ? { section } : {};
            this.$router.push({ path, query });
            return; // skip showing the results page
          }
        }

      } catch (err) {
          this.error = this.$t('searchResults.error.generic');
          this.allResults = [];
      } finally {
          this.isLoading = false;
      }
    },

    /**
     * Generates a unique key for deduplication across languages.
     * Same item searched in EN vs FR will have the same structure ID / embassy country.
     */
    _getResultUniqueKey(result) {
      if (result.type === 'Organization') {
        const structureId = result.originalData?.related_data?.structure?.id;
        return structureId ? `structure-${structureId}` : result.uniqueId;
      } else if (result.type === 'Embassy') {
        return result.country ? `embassy-${result.country}` : result.uniqueId;
      }
      return result.uniqueId;
    },

    /**
     * Process search response without overwriting this.didYouMean / this.searchIntent.
     * Used for the alternate language response during merge.
     */
    _processSearchResponseClean(resData) {
        const results = resData.results;
        if (!results) return [];
        
        let processed = [];
        for (const key in results) {
          if (results[key]?.items) {
            results[key].items.forEach(item => processed.push(this._normalizeResult(item)));
          }
        }

        const uniqueResultsMap = new Map();
        processed.forEach(result => {
          let uniqueKey;
          if (result.type === 'Organization') {
              const structureId = result.originalData?.related_data?.structure?.id;
              if (!structureId) return;
              uniqueKey = `structure-${structureId}`;
          } else if (result.type === 'Embassy') {
              if (!result.country) return;
              uniqueKey = `embassy-${result.country}`;
          } else {
              uniqueKey = result.uniqueId;
          }
          if (!uniqueKey) return;
          const existing = uniqueResultsMap.get(uniqueKey);
          const newScore = result.originalData.match_score || 0;
          if (!existing || newScore > (existing.originalData.match_score || 0)) {
              uniqueResultsMap.set(uniqueKey, result);
          }
        });
        
        return Array.from(uniqueResultsMap.values()).sort((a, b) => (b.originalData.match_score || 0) - (a.originalData.match_score || 0));
    },

    /**
     * Re-fetches the SAME results in a new display language.
     * Uses `lang` param = the language that originally found results (so backend searches correct fields).
     * Uses `Accept-Language` = new UI language (so backend returns translated field values).
     * Falls back to parallel bilingual search if the lang-param approach returns no results.
     */
    async refetchInLanguage() {
      // performSearch already handles bilingual merge — just re-run it
      // The currentLanguage computed will reflect the new locale
      return this.performSearch();
    },
    _processSearchResponse(resData) {
        this.didYouMean = resData.did_you_mean;
        this.searchIntent = resData.intent;
        
        const results = resData.results;
        if (!results) return [];
        
        let processed = [];
        for (const key in results) {
          if (results[key]?.items) {
            results[key].items.forEach(item => processed.push(this._normalizeResult(item)));
          }
        }

        const uniqueResultsMap = new Map();
        processed.forEach(result => {
          let uniqueKey;
          if (result.type === 'Organization') {
              const structureId = result.originalData?.related_data?.structure?.id;
              if (!structureId) return;
              uniqueKey = `structure-${structureId}`;
          } else if (result.type === 'Embassy') {
              if (!result.country) return;
              uniqueKey = `embassy-${result.country}`;
          } else {
              uniqueKey = result.uniqueId;
          }
          
          if (!uniqueKey) return;

          const existing = uniqueResultsMap.get(uniqueKey);
          const newScore = result.originalData.match_score || 0;

          if (!existing || newScore > (existing.originalData.match_score || 0)) {
              uniqueResultsMap.set(uniqueKey, result);
          }
        });
        
        return Array.from(uniqueResultsMap.values()).sort((a, b) => (b.originalData.match_score || 0) - (a.originalData.match_score || 0));
    },
    switchToResultsLanguage() {
      if (this.resultsLanguage && this.resultsLanguage !== this.currentLanguage) {
        this.$i18n.locale = this.resultsLanguage;
        localStorage.setItem('user-language', this.resultsLanguage);
        this.searchedInAlternateLang = false;
      }
    },
    setStructureStore(result) {
        const store = useStructureStore();
        const rd = result.originalData.related_data;
        if (!rd || !rd.structure) return;
        store.setStructureData(rd.structure);
        store.setCurrentStructure(rd.structure);
        store.setStructureBoss(rd.boss || {});
        store.setStructureBossContact(rd.boss_contacts || {});
        store.setStructureContact(rd.contacts || {});
        store.setStructureMissions(rd.missions || []);
        store.setStructureRegionals(rd.regionals || []);
        store.setStatePersonnels(rd.personnels || []);
    },
    async navigateToResult(result, clickedSection = null) {
      if (this.isNavigating) return;
      this.isNavigating = true;

      // If results came from alternate language, switch app to that language before navigating
      const navLang = this.resultsLanguage || this.currentLanguage;
      if (this.resultsLanguage && this.resultsLanguage !== this.currentLanguage) {
        this.$i18n.locale = this.resultsLanguage;
        localStorage.setItem('user-language', this.resultsLanguage);
      }

      try {
        if (result.type === 'Public Personality') {
            const store = usePublicPersonalityStore();
            store.clearCurrentPersonality();
            const config = { headers: { 'Accept-Language': navLang } };
            const [personRes, contactRes] = await Promise.all([
                axios.get(`${BaseURL}/public-personalities/${result.id}/`, config),
                axios.get(`${BaseURL}/public-personality-contacts/${result.id}/`, config)
            ]);
            if (!personRes.data) throw new Error('Personality details fetch failed.');
            store.setCurrentPersonality({ ...personRes.data, contacts: contactRes.data || {} });
            this.$router.push({
                name: 'public-personality-detail',
                params: { lang: navLang, name: encodeURIComponent(result.name) }
            });

        } else if (result.type === 'Embassy') {
            if (!result.country) throw new Error("Missing embassy country for navigation.");
            
            const embassyStore = useEmbassyStore();
            embassyStore.addOrUpdateEmbassy(result.originalData);

            this.$router.push({
                name: 'embassy-detail',
                params: { lang: navLang, name: result.country }
            });

        } else { // Handle 'Organization'
            if (!result?.originalData?.related_data?.structure) throw new Error("Missing structure data.");
            // Clear store and set only the structure ID + name.
            // DisplayerPage will refetch all data in the correct language.
            const store = useStructureStore();
            store.clearCurrentStructure();
            const structure = result.originalData.related_data.structure;
            store.currentStructureId = structure.id;
            store.currentStructureName = structure.name;
            const path = `/${navLang}/structure-details/${encodeURIComponent(structure.name)}`;
            const query = clickedSection ? { section: clickedSection } : {};
            this.$router.push({ path, query });
        }
      } catch (error) {
          this.error = this.$t('searchResults.error.navigation');
      } finally {
          this.isNavigating = false;
      }
    },
    clearSearch() {
      this.searchTerm = ''; this.allResults = []; this.didYouMean = null;
      this.$router.push({ path: this.$route.path, query: {} });
    },
    goToPage(page) {
        if (typeof page === 'string' || page < 1 || page > this.totalPages) return;
        this.currentPage = page;
        window.scrollTo(0, 0);
    },
    searchSuggestion(suggestion) {
        this.searchTerm = suggestion;
        this.$router.push({ query: { q: suggestion } });
        this.performSearch();
    }
  },
  watch: {
    '$route.query.q'(newVal) {
        this.searchTerm = newVal || '';
        if (this.searchTerm) this.performSearch();
        else this.clearSearch();
    },
    currentLanguage(newLang) {
      if (this.searchTerm && this.allResults.length > 0) {
        // Re-fetch same entities in new language (don't re-search from scratch)
        this.refetchInLanguage(newLang);
      } else if (this.searchTerm) {
        this.performSearch();
      }
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cursor-pointer { cursor: pointer; }

/* Conformity star rating in search results */
.conformity-stars-inline {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 4px;
}
.conformity-star-filled { color: #f6ad55; font-size: 13px; }
.conformity-star-empty { color: #cbd5e0; font-size: 13px; }
.conformity-verified-icon {
  color: #1877f2;
  font-size: 14px;
  margin-left: 5px;
}
</style>